// ═══════════════════════════════════════════════════════════════
// zipReader.ts – ZIP kibontása a böngészőben, külső könyvtár nélkül
//
// A kötegelt feltöltéshez: a ZIP-ben lévő fájlok ugyanúgy kerülnek a listába, mintha egyenként
// húzták volna be őket. A tárolt (0) és a deflate (8) módot ismeri (Windows, macOS, 7-Zip alapértelmezés);
// a deflate-et a böngésző natív DecompressionStream('deflate-raw') API-ja bontja ki.
// Kihagyja a mappa-bejegyzéseket, a macOS (__MACOSX, ._*) és a rejtett (.DS_Store, Thumbs.db) fájlokat.
// Titkosított ZIP-et nem bont ki (hibát ad).
// ═══════════════════════════════════════════════════════════════

const EOCD_SIG = 0x06054b50;
const CEN_SIG = 0x02014b50;
const LOC_SIG = 0x04034b50;

const MIME: Record<string, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv',
  txt: 'text/plain',
  odt: 'application/vnd.oasis.opendocument.text',
  ods: 'application/vnd.oasis.opendocument.spreadsheet',
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
  zip: 'application/zip',
};
export const mimeOf = (name: string) => MIME[(name.split('.').pop() || '').toLowerCase()] || 'application/octet-stream';

export const isZipFile = (f: File) => /\.zip$/i.test(f.name) || f.type === 'application/zip' || f.type === 'application/x-zip-compressed';

const isJunk = (path: string) => {
  const parts = path.split('/');
  const base = parts[parts.length - 1];
  return parts.includes('__MACOSX') || base.startsWith('._') || /^(\.ds_store|thumbs\.db|desktop\.ini)$/i.test(base);
};

async function inflateRaw(bytes: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream('deflate-raw');
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(ds);
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

export interface ZipEntryFile {
  /** a teljes útvonal a ZIP-en belül (mappákkal) */
  path: string;
  file: File;
}

/**
 * A ZIP fájljai File objektumként (a fájlnév a ZIP-beli utolsó elem; az útvonal külön).
 * Hibák: nem ZIP / sérült / titkosított / nem támogatott tömörítés.
 */
export async function readZipFiles(zip: File): Promise<ZipEntryFile[]> {
  const buf = await zip.arrayBuffer();
  const bytes = new Uint8Array(buf);
  const view = new DataView(buf);
  let eocd = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 65557); i--) {
    if (view.getUint32(i, true) === EOCD_SIG) { eocd = i; break; }
  }
  if (eocd < 0) throw new Error(`„${zip.name}”: nem érvényes ZIP fájl`);
  const count = view.getUint16(eocd + 10, true);
  let off = view.getUint32(eocd + 16, true);
  const out: ZipEntryFile[] = [];
  for (let i = 0; i < count; i++) {
    if (view.getUint32(off, true) !== CEN_SIG) throw new Error(`„${zip.name}”: sérült ZIP (központi könyvtár)`);
    const flags = view.getUint16(off + 8, true);
    const method = view.getUint16(off + 10, true);
    const compSize = view.getUint32(off + 20, true);
    const nameLen = view.getUint16(off + 28, true);
    const extraLen = view.getUint16(off + 30, true);
    const commentLen = view.getUint16(off + 32, true);
    const localOff = view.getUint32(off + 42, true);
    const nameBytes = bytes.subarray(off + 46, off + 46 + nameLen);
    // UTF-8 jelző (11. bit) nélkül is UTF-8-ként próbáljuk (a Windows régi ZIP-jei CP437/852 – ritka)
    const path = new TextDecoder('utf-8').decode(nameBytes).replace(/\\/g, '/');
    off += 46 + nameLen + extraLen + commentLen;
    if (path.endsWith('/') || isJunk(path)) continue;
    if (flags & 0x0001) throw new Error(`„${zip.name}”: jelszóval védett ZIP – kérjük, csomagolja ki, és a fájlokat töltse fel.`);
    if (view.getUint32(localOff, true) !== LOC_SIG) throw new Error(`„${zip.name}”: sérült ZIP („${path}”)`);
    const start = localOff + 30 + view.getUint16(localOff + 26, true) + view.getUint16(localOff + 28, true);
    const comp = bytes.subarray(start, start + compSize);
    let data: Uint8Array;
    if (method === 0) data = comp.slice();
    else if (method === 8) data = await inflateRaw(comp);
    else throw new Error(`„${zip.name}”: nem támogatott tömörítés („${path}”)`);
    const base = path.split('/').pop() || path;
    out.push({ path, file: new File([data as BlobPart], base, { type: mimeOf(base) }) });
  }
  return out;
}
